import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WeekSelector from '../components/WeekSelector';
import ClassAttendanceResultsTable from '../components/ClassAttendanceResultsTable';
import {useRecoilValue, useSetRecoilState} from "recoil";
import {topMenuTypeState} from "../../../recoil/menuState/topMenuState";
import {getClassLesson, getClassWeeklyLearningData, weeklyLearningData} from "../../../network/Endpoint";
import {mainClassState} from "../../../recoil/classes/classesState";
import SideMenu from "../../../components/SideMenu";

const ClassAttendancePage = () => {
  const mainClass = useRecoilValue(mainClassState);
  // 선택된 주간의 시작일과 종료일
  const [selectedWeek, setSelectedWeek] = useState({
    startDate: '',
    endDate: ''
  });

  // 학생 결과 데이터
  const [studentResults, setStudentResults] = useState([]);
  
  // 데이터 로딩 상태
  const [loading, setLoading] = useState(false);
  
  // 클래스 ID (실제로는 URL 파라미터나 상태로부터 가져와야 함)
  const classId = 1; // 임시값, 실제 구현 시 URL 파라미터 등에서 가져와야 함

  const setMenuType = useSetRecoilState(topMenuTypeState);

  useEffect(() =>  {
    if(setMenuType){
      setMenuType("classAttendance");
    }
  },[setMenuType])

  // 주간이 변경되면 API 호출하여 데이터 가져오기
  useEffect(() => {
    if (selectedWeek.startDate && selectedWeek.endDate) {
      fetchStudentResults();
    }
  }, [selectedWeek]);

  // 처음 마운트될 때 현재 날짜를 기준으로 이번 주 설정
  useEffect(() => {
    const now = new Date();
    const currentDay = now.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
    const diffToMonday = currentDay === 0 ? -6 : 1 - currentDay; // 월요일까지의 차이 계산
    
    const mondayDate = new Date(now);
    mondayDate.setDate(now.getDate() + diffToMonday);
    
    const sundayDate = new Date(mondayDate);
    sundayDate.setDate(mondayDate.getDate() + 6);
    
    // 날짜 포맷 변환 (YYYY.MM.DD)
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    };
    setSelectedWeek({
      startDate: formatDate(mondayDate),
      endDate: formatDate(sundayDate)
    });
  }, []);

  // API 호출하여 학생 결과 데이터 가져오기
  const fetchStudentResults = async () => {
    setLoading(true);
    
    // 로딩 타이머 (0.2초 이상 로딩이 지속될 때만 로딩 UI 표시)
    const loadingTimer = setTimeout(() => {
      setLoading(true);
    }, 300);
    
    try {
      // API 날짜 포맷 변환 (YYYY.MM.DD -> YYYY-MM-DD)
      const convertDateFormat = (dateString) => {
        return dateString.replace(/\./g, '-');
      };
      const body =
          { classId: mainClass.classId,
            params: {
              startDate: convertDateFormat(selectedWeek.startDate),
              endDate: convertDateFormat(selectedWeek.endDate)
            }};
      const res = await getClassWeeklyLearningData(body);
      // API 응답 데이터 처리
      if (res.data && res.data.status.code === "Success") {
        // 데이터 변환하여 저장
        const transformedData = res.data.data.classWeeklyLearningResults.map(student => {
          // 일별 학습 횟수 데이터를 요일 약자로 변환
          const dailyRecordCounts = student.dailyRecordCounts;
          
          return {
            id: student.studentId,
            number: student.number,
            name: student.name,
            totalStudyTime: formatStudyTime(student.totalStudyTime),
            averageAccuracy: student.averageAccuracy ? `${student.averageAccuracy.toFixed(1)}%` : '0.0%',
            weeklyStageCnt: student.weeklyStageCnt,
            // 요일별 학습 횟수 (0인 경우 '-'로 표시)
            mon: dailyRecordCounts.MONDAY > 0 ? dailyRecordCounts.MONDAY.toString() : '-',
            tue: dailyRecordCounts.TUESDAY > 0 ? dailyRecordCounts.TUESDAY.toString() : '-',
            wed: dailyRecordCounts.WEDNESDAY > 0 ? dailyRecordCounts.WEDNESDAY.toString() : '-',
            thu: dailyRecordCounts.THURSDAY > 0 ? dailyRecordCounts.THURSDAY.toString() : '-',
            fri: dailyRecordCounts.FRIDAY > 0 ? dailyRecordCounts.FRIDAY.toString() : '-',
            sat: dailyRecordCounts.SATURDAY > 0 ? dailyRecordCounts.SATURDAY.toString() : '-',
            sun: dailyRecordCounts.SUNDAY > 0 ? dailyRecordCounts.SUNDAY.toString() : '-'
          };
        });
        
        setStudentResults(transformedData);
      } else {
        console.error('API 응답 형식이 올바르지 않습니다:', res.data);
        setStudentResults([]);
      }
    } catch (error) {
      console.error('학생 결과를 가져오는 중 오류 발생:', error);
      setStudentResults([]);
    } finally {
      clearTimeout(loadingTimer);
      setLoading(false);
    }
  };
  
  // 학습 시간을 포맷팅하는 함수 (초 -> 분:초)
  const formatStudyTime = (seconds) => {
    if (!seconds || seconds === 0) return '0분0초';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    return `${minutes}분${remainingSeconds}초`;
  };

  return (
    <div className="flex flex-col items-center w-full max-md:max-w-full bg-[#F8F8F8] min-h-screen">

      {/* 주간 선택 컴포넌트 */}
      <WeekSelector 
        selectedWeek={selectedWeek}
        onWeekChange={setSelectedWeek}
        mainClass={mainClass}
      />
      
      {/* 학생 결과 테이블 */}
      <ClassAttendanceResultsTable
        loading={loading}
        studentResults={studentResults}
        selectedWeek={selectedWeek}
      />
      <SideMenu></SideMenu>

    </div>
  );
};

export default ClassAttendancePage;